import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface CentroCallDisplayState {
  dueId?: number | string;
  phoneNo?: string;
  fullName?: string;
  Status?: string;
  clientId?: string;
  callingType: 'centro';
  customerAnswered?: boolean;
  callDirection?: 'campaign' | 'manual' | 'inbound';
  recordId?: string;
  crmDataId?: string;
  campaignName?: string;
  [key: string]: any;
}

const PHONE_KEYS = ['phoneNo', 'phone', 'DispalyPhoneNumber', 'currentPhone', 'phoneno', 'PhoneNo'];

@Injectable({ providedIn: 'root' })
export class CentroDialerFlowService {
  private lastNavigationKey = '';
  private lastNavigationAt = 0;

  constructor(private http: HttpClient, private router: Router) {}

  sanitizeDueId(value: unknown): number | string | undefined {
    if (value == null || value === '') return undefined;
    const raw = String(value).trim();
    if (!raw || raw === '0') return undefined;
    if (/^\d+$/.test(raw)) return Number(raw);
    return raw;
  }

  getPhoneNumber(data: any): string {
    if (!data) return '';
    for (const key of PHONE_KEYS) {
      const digits = String(data[key] || '').replace(/\D/g, '').slice(-10);
      if (digits.length >= 10) return digits;
    }
    const pending = data?.pendingCall?.phone;
    const pendingDigits = String(pending || '').replace(/\D/g, '').slice(-10);
    return pendingDigits.length >= 10 ? pendingDigits : '';
  }

  resolveDueId(data: any): number | string | undefined {
    const direct = this.sanitizeDueId(data?.dueId ?? data?.DueId);
    if (direct) return direct;
    const clientId = String(data?.clientId || data?.clientid || '').trim();
    if (clientId.includes(':')) {
      return this.sanitizeDueId(clientId.split(':').pop());
    }
    return undefined;
  }

  resolveFullName(data: any): string {
    if (!data || typeof data !== 'object') return '';
    const candidates = [
      data.fullName,
      data.name,
      data.customerName,
      data.CustomerName,
      data.callerName,
      data.f1,
      data.F1,
      data?.callData?.crmRes?.data?.f1,
      data?.callData?.crmRes?.data?.F1,
    ];
    for (const name of candidates) {
      const trimmed = name != null ? String(name).trim() : '';
      if (trimmed && trimmed.toLowerCase() !== 'unknown') return trimmed;
    }
    return '';
  }

  enrichCallDisplayData(data: any): CentroCallDisplayState {
    const status = data?.Status || data?.status || 'CONNECTED';
    const normalized = this.normalizeCallData(data, status);
    const fullName = this.resolveFullName(normalized);
    if (fullName) normalized.fullName = fullName;
    return normalized;
  }

  normalizeCallData(data: any, status = 'CONNECTED'): CentroCallDisplayState {
    const phoneNo = this.getPhoneNumber(data);
    const dueId = this.resolveDueId(data);
    const out: CentroCallDisplayState = {
      ...data,
      callingType: 'centro',
      Status: status,
      phoneNo,
    };
    if (dueId) {
      out.dueId = dueId;
      out['DueId'] = dueId;
    }
    const fullName = String(data?.fullName || data?.name || data?.customerName || '').trim();
    if (fullName) out.fullName = fullName;
    return out;
  }

  enrichFromApiContext(callData: any, context: any): CentroCallDisplayState {
    const contextPhone = this.getPhoneNumber(context);
    const callPhone = this.getPhoneNumber(callData);
    const merged = {
      ...callData,
      phoneNo: contextPhone || callPhone,
      recordId: context?.recordId || callData?.recordId,
      crmDataId: context?.crmDataId || callData?.crmDataId,
      campaignName: context?.campaignName || callData?.campaignName,
      pendingCall: context?.pendingCall || callData?.pendingCall,
    };
    if (contextPhone && callPhone && contextPhone !== callPhone) {
      merged.fullName = '';
      merged.dueId = undefined;
      merged.DueId = undefined;
      merged.clientId = context?.clientId || callData?.clientId || '';
    }
    return this.enrichCallDisplayData(merged);
  }

  async lookupCase(callData: CentroCallDisplayState, fallbackStatus = 'CONNECTED'): Promise<CentroCallDisplayState | null> {
    const phoneNumber = this.getPhoneNumber(callData);
    const dueId = callData?.dueId || callData?.['DueId'] || this.resolveDueId(callData);
    const clientId = String(callData?.clientId || '').trim();
    if (!phoneNumber && !dueId && !clientId) return null;
    try {
      const response: any = await firstValueFrom(
        this.http.post(
          `${environment.api_url}/api/ozonetel-webhook/lookup-case`,
          {
            ...(phoneNumber ? { phoneNumber } : {}),
            ...(clientId ? { clientId } : {}),
            ...(dueId ? { dueId } : {}),
          },
          { withCredentials: true }
        )
      );
      if (response?.resultShort === 'success' && response.data) {
        return this.normalizeCallData(
          {
            ...callData,
            ...response.data,
            fullName: response.data?.fullName || callData?.fullName,
            Status: fallbackStatus,
          },
          fallbackStatus
        );
      }
    } catch (_e) {}
    return null;
  }

  async openCaseFromCall(callData: CentroCallDisplayState, fallbackStatus = 'CONNECTED'): Promise<CentroCallDisplayState | null> {
    const callStatus = callData?.Status || fallbackStatus;
    let normalized = this.normalizeCallData({ ...callData, Status: callStatus }, callStatus);
    console.log('[CentroDialerFlow] openCaseFromCall:start', {
      status: callStatus,
      direction: callData?.callDirection || '',
      phoneNo: normalized.phoneNo || '',
      dueId: normalized.dueId || '',
      customerAnswered: !!callData?.customerAnswered,
    });
    if (!normalized.phoneNo || !normalized.fullName || !normalized.dueId) {
      const lookup = await this.lookupCase(normalized, callStatus);
      if (lookup) {
        normalized = this.normalizeCallData(lookup, callStatus);
        console.log('[CentroDialerFlow] openCaseFromCall:lookup-hit', {
          phoneNo: normalized.phoneNo || '',
          dueId: normalized.dueId || '',
        });
      }
    }
    if (!normalized.phoneNo && !normalized.dueId) {
      console.warn('[CentroDialerFlow] openCaseFromCall:missing-phone-and-dueId');
      return null;
    }
    const shouldOpenTimeline =
      callData.callDirection === 'campaign'
        ? !!callData.customerAnswered
        : callStatus === 'CONNECTED' || callStatus === 'CALLING' || !!callData.customerAnswered;
    if (shouldOpenTimeline && normalized.dueId) {
      console.log('[CentroDialerFlow] openCaseFromCall:navigate', {
        dueId: normalized.dueId,
        phoneNo: normalized.phoneNo || '',
      });
      this.openCase(normalized);
    } else {
      console.log('[CentroDialerFlow] openCaseFromCall:skip-navigate', {
        shouldOpenTimeline,
        dueId: normalized.dueId || '',
        status: callStatus,
      });
    }
    return normalized;
  }

  openCase(callData: CentroCallDisplayState): boolean {
    const normalized = this.normalizeCallData(callData, callData?.Status || 'CONNECTED');
    const dueId = this.sanitizeDueId(normalized.dueId ?? normalized['DueId']);
    if (!dueId || normalized.Status === 'CALLING') {
      console.warn('[CentroDialerFlow] openCase:blocked', {
        dueId: dueId || '',
        status: normalized.Status || '',
      });
      return false;
    }
    normalized.dueId = dueId;
    normalized['DueId'] = dueId;
    const navigationKey = `${dueId}:${this.getPhoneNumber(normalized)}`;
    const now = Date.now();
    if (navigationKey === this.lastNavigationKey && now - this.lastNavigationAt < 1500) {
      console.log('[CentroDialerFlow] openCase:deduped-navigation', { navigationKey });
      return true;
    }
    this.lastNavigationKey = navigationKey;
    this.lastNavigationAt = now;
    try {
      localStorage.setItem('callingType', 'centro');
      localStorage.setItem('incomingCall', 'true');
    } catch (_e) {}
    const queryParams: Record<string, string> = {
      callerNo: this.getPhoneNumber(normalized),
      phoneNo: this.getPhoneNumber(normalized),
      callingType: 'centro',
    };
    if (normalized.fullName) queryParams['fullName'] = normalized.fullName;
    const clientId = normalized.clientId ? String(normalized.clientId) : '';
    if (clientId) queryParams['clientId'] = clientId;
    console.log('[CentroDialerFlow] openCase:navigate', { dueId, queryParams });
    void this.router.navigate(['/timeline', dueId], { queryParams, replaceUrl: true });
    return true;
  }
}
